/**
 * device.ts —— 设备运行时状态（内存）
 *
 * 摄像头 / 麦克风是一类特殊资源：它们**独占硬件**，
 * 同一时刻只能有一路流。所以需要一处地方统一持有、统一释放，
 * 避免 API 各自 getStream / stopStream 导致流泄漏（浏览器会亮着摄像头灯不放）。
 *
 * 这里还兜住一个真实世界的坑：MediaStream 不参与 GC 的引用计数很弱，
 * 忘了 stop 的 track 会一直占着设备。提供 releaseAll() 在页面卸载时调用。
 */

export interface CameraState {
  stream: MediaStream | null;
  /** 最近一次拍照产生的 blob（供 capture 之后引用） */
  lastPhoto: Blob | null;
  lastPhotoName: string;
}

class DeviceRuntime {
  readonly camera: CameraState = { stream: null, lastPhoto: null, lastPhotoName: '' };
  private micStream: MediaStream | null = null;

  get isCameraOn(): boolean {
    return this.camera.stream !== null;
  }

  get isMicOn(): boolean {
    return this.micStream !== null;
  }

  setMic(stream: MediaStream | null): void {
    if (this.micStream && this.micStream !== stream) {
      this.micStream.getTracks().forEach((t) => t.stop());
    }
    this.micStream = stream;
  }

  /** 页面卸载时释放全部硬件 —— 否则摄像头指示灯会一直亮 */
  releaseAll(): void {
    this.camera.stream?.getTracks().forEach((t) => t.stop());
    this.camera.stream = null;
    this.micStream?.getTracks().forEach((t) => t.stop());
    this.micStream = null;
  }
}

export const deviceRuntime = new DeviceRuntime();
